/**
 * Tiny pixel-art toolkit shared by skill, class and item icons: a 24 x 24 grid, drawing
 * primitives, and a renderer that adds a dark outline and a bevel.
 */
export const N = 24;

export type Grid = (string | null)[][];

export interface Palette {
  bg: [string, string];
  main: string;
  light: string;
  dark: string;
}

export const ELEMENT: Record<string, Palette> = {
  steel: { bg: ["#5a2a24", "#1e0f0d"], main: "#c9ced6", light: "#f2f4f7", dark: "#6c7380" },
  shield: { bg: ["#3b2c1c", "#140e08"], main: "#b8864a", light: "#e8c07a", dark: "#6b4a2a" },
  rage: { bg: ["#6b1d14", "#200805"], main: "#ff7a3a", light: "#ffd08a", dark: "#a8341c" },
  poison: { bg: ["#1f3d1c", "#0a150a"], main: "#7ad04a", light: "#c8f58a", dark: "#3a7a24" },
  shadow: { bg: ["#2a1d3d", "#0c0814"], main: "#a17ad8", light: "#dcc8ff", dark: "#5a3d8a" },
  fire: { bg: ["#6b2410", "#1c0804"], main: "#ff8a24", light: "#ffe07a", dark: "#c43c10" },
  frost: { bg: ["#15374f", "#060f18"], main: "#7ec8f2", light: "#e0f6ff", dark: "#2f7ab0" },
  arcane: { bg: ["#2d1f55", "#0b0718"], main: "#b48cff", light: "#f0e0ff", dark: "#6a45c9" },
  holy: { bg: ["#5a4410", "#1a1204"], main: "#ffd35a", light: "#fff6c8", dark: "#c89422" },
  nature: { bg: ["#1d4a2a", "#08160c"], main: "#6fdc7a", light: "#d4ffd0", dark: "#2f8a44" },
};

export class Pix {
  readonly g: Grid = Array.from({ length: N }, () => Array<string | null>(N).fill(null));

  set(x: number, y: number, c: string) {
    const xi = Math.round(x);
    const yi = Math.round(y);
    if (xi >= 0 && yi >= 0 && xi < N && yi < N) (this.g[yi] as (string | null)[])[xi] = c;
  }

  rect(x: number, y: number, w: number, h: number, c: string) {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.set(x + i, y + j, c);
  }

  line(x0: number, y0: number, x1: number, y1: number, c: string, width = 1) {
    const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let s = 0; s <= steps; s++) {
      const x = x0 + ((x1 - x0) * s) / steps;
      const y = y0 + ((y1 - y0) * s) / steps;
      for (let w = 0; w < width; w++) this.set(x + (width > 1 ? w - (width - 1) / 2 : 0), y, c);
    }
  }

  disc(cx: number, cy: number, r: number, c: string) {
    for (let y = -r; y <= r; y++)
      for (let x = -r; x <= r; x++)
        if (x * x + y * y <= r * r + r * 0.6) this.set(cx + x, cy + y, c);
  }

  ring(cx: number, cy: number, r: number, c: string) {
    for (let a = 0; a < 360; a += 4) {
      const t = (a * Math.PI) / 180;
      this.set(cx + Math.cos(t) * r, cy + Math.sin(t) * r, c);
    }
  }

  arc(cx: number, cy: number, r: number, from: number, to: number, c: string) {
    for (let a = from; a <= to; a += 3) {
      const t = (a * Math.PI) / 180;
      this.set(cx + Math.cos(t) * r, cy + Math.sin(t) * r, c);
    }
  }
}

// ------------------------------------------------------------------ glyph pieces

export function sword(p: Pix, pal: Palette, x0: number, y0: number, x1: number, y1: number) {
  p.line(x0, y0, x1, y1, pal.main, 2);
  p.line(x0 + 1, y0, x1 + 1, y1, pal.light);
  // Guard and grip at the (x1, y1) end.
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  p.line(x1 - nx * 3, y1 - ny * 3, x1 + nx * 3, y1 + ny * 3, "#c9a94a", 1);
  p.line(x1, y1, x1 + (dx / len) * 3, y1 + (dy / len) * 3, "#6b4a2a", 2);
}

export function dagger(p: Pix, pal: Palette, x0: number, y0: number, x1: number, y1: number) {
  p.line(x0, y0, x1, y1, pal.main);
  p.line(x0 + 1, y0 + 1, x1, y1, pal.light);
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  p.line(
    x1 - (dy / len) * 2,
    y1 + (dx / len) * 2,
    x1 + (dy / len) * 2,
    y1 - (dx / len) * 2,
    "#c9a94a",
  );
  p.line(x1, y1, x1 + (dx / len) * 3, y1 + (dy / len) * 3, "#4a3423", 1);
}

export function arrow(p: Pix, color: string, x0: number, y0: number, x1: number, y1: number) {
  p.line(x0, y0, x1, y1, "#8a6a45");
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  for (let i = 0; i < 4; i++) {
    p.line(
      x1 - ux * i - uy * (3 - i),
      y1 - uy * i + ux * (3 - i),
      x1 - ux * i + uy * (3 - i),
      y1 - uy * i - ux * (3 - i),
      color,
    );
  }
  // Fletching.
  p.line(x0, y0, x0 - uy * 2 + ux, y0 + ux * 2 + uy, "#e6dcc0");
  p.line(x0, y0, x0 + uy * 2 + ux, y0 - ux * 2 + uy, "#e6dcc0");
}

export function shieldShape(p: Pix, pal: Palette, cx: number, cy: number, w: number, h: number) {
  for (let y = 0; y < h; y++) {
    const t = y / h;
    const half = t < 0.55 ? w / 2 : (w / 2) * (1 - (t - 0.55) / 0.45) + 0.5;
    for (let x = -half; x <= half; x++)
      p.set(cx + x, cy - h / 2 + y, Math.abs(x) >= half - 1 ? pal.dark : pal.main);
  }
  p.line(cx, cy - h / 2 + 1, cx, cy + h / 2 - 2, pal.light);
}

export function flame(p: Pix, pal: Palette, cx: number, cy: number, r: number) {
  p.disc(cx, cy, r, pal.dark);
  p.disc(cx, cy - 1, r - 1, pal.main);
  for (let i = 0; i < r; i++) p.line(cx - r + i + 1, cy - r - i + 1, cx, cy - r * 2 + 1, pal.main);
  p.disc(cx, cy, Math.max(1, r - 3), pal.light);
}

export function star(p: Pix, c: string, cx: number, cy: number, r: number) {
  p.line(cx - r, cy, cx + r, cy, c);
  p.line(cx, cy - r, cx, cy + r, c);
  p.line(cx - r * 0.6, cy - r * 0.6, cx + r * 0.6, cy + r * 0.6, c);
  p.line(cx - r * 0.6, cy + r * 0.6, cx + r * 0.6, cy - r * 0.6, c);
}

export function cross(p: Pix, c: string, cx: number, cy: number, s: number) {
  p.rect(cx - 1, cy - s, 3, s * 2 + 1, c);
  p.rect(cx - s, cy - 1, s * 2 + 1, 3, c);
}

/** Renders a drawn grid over a background into a data URL (a bevelled tile unless `bevel` is off). */
export function renderIcon(
  p: Pix,
  background: (ctx: CanvasRenderingContext2D) => void,
  bevel = true,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = N;
  canvas.height = N;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

  background(ctx);

  // Outline: every empty pixel next to a glyph pixel becomes near-black.
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      if (p.g[y]?.[x]) continue;
      const near = [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ].some(([dx, dy]) => p.g[y + (dy as number)]?.[x + (dx as number)]);
      if (near) {
        ctx.fillStyle = "rgba(8,6,4,0.85)";
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const c = p.g[y]?.[x];
      if (!c) continue;
      ctx.fillStyle = c;
      ctx.fillRect(x, y, 1, 1);
    }
  }
  if (!bevel) return canvas.toDataURL();
  // Bevel.
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  ctx.fillRect(0, 0, N, 1);
  ctx.fillRect(0, 0, 1, N);
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.fillRect(0, N - 1, N, 1);
  ctx.fillRect(N - 1, 0, 1, N);

  return canvas.toDataURL();
}
